/* eslint-disable react-refresh/only-export-components */
import { useState } from 'react';
import { useSnapshot } from 'valtio';

import { OrganizationInfo, OrganizationPlanType } from '@cartwhl/common/types';
import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import * as getStartedCookie from '@cartwhl/common/utils/getStartedCookie';
import { formatOrganizationPlanType } from '@cartwhl/common/utils/organizationWorkspace';
import FormattedDate from '@cartwhl/ui/atoms/FormattedDate';
import Video from '@cartwhl/ui/atoms/Video';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { userStore } from 'stores';

interface WelcomeModalProps {
  close: () => void;
}

export type WelcomeModalVariant = 'team' | 'individual';

type TeamWelcomeStep = 1 | 2;

const TRIAL_WELCOME_STORAGE_KEY = 'studioTrialWelcomeSeen';
const WELCOME_MODAL_PREVIEW_PARAM = 'welcomePreview';

const WELCOME_PLAN_TYPES: Record<WelcomeModalVariant, OrganizationPlanType> = {
  team: 'team_pro',
  individual: 'individual_pro',
};

const TEAM_WELCOME_TITLE = "You're all set! Now invite your team!";
const INDIVIDUAL_WELCOME_TITLE = "You're all set with your free trial!";
const WELCOME_VIDEO_CARTWHEEL_INTRO =
  'https://resources.getcartwheel.com/share/welcome-modal/cartwheel_intro.webm';
const WELCOME_VIDEO_INVITE_TEAM_INTRO =
  'https://resources.getcartwheel.com/share/welcome-modal/invite_team_intro.webm';

/** Workspace org plans that use the Studio trial welcome modal (team vs individual copy). */
const TRIAL_WELCOME_WORKSPACE_PLANS = new Set<OrganizationPlanType>([
  'individual_basic',
  'individual_pro',
  'team_standard',
  'team_pro',
  'team_scale',
]);

function welcomeModalVariantForWorkspaceTrialPlan(
  planType: OrganizationPlanType | undefined,
): WelcomeModalVariant | null {
  if (!planType || !TRIAL_WELCOME_WORKSPACE_PLANS.has(planType)) {
    return null;
  }
  return planType.startsWith('individual') ? 'individual' : 'team';
}

function isTrialWelcomeWorkspacePlan(
  planType: OrganizationPlanType | undefined,
): planType is
  | 'individual_basic'
  | 'individual_pro'
  | 'team_standard'
  | 'team_pro'
  | 'team_scale' {
  return planType != null && TRIAL_WELCOME_WORKSPACE_PLANS.has(planType);
}

export default function WelcomeModal({ close }: WelcomeModalProps) {
  const { organizationInfo } = useSnapshot(userStore.state);
  const variant = getWelcomeModalVariant(organizationInfo) ?? 'team';
  const [teamStep, setTeamStep] = useState<TeamWelcomeStep>(1);

  const isTeamFlow = variant === 'team';

  const showTrialIntro =
    variant === 'individual' || (isTeamFlow && teamStep === 1);
  const showTeamChecklist = isTeamFlow && teamStep === 1;
  const showIndividualTips =
    variant === 'individual' || (isTeamFlow && teamStep === 2);

  const modalTitle =
    variant === 'individual' || (isTeamFlow && teamStep === 2)
      ? INDIVIDUAL_WELCOME_TITLE
      : TEAM_WELCOME_TITLE;

  const planType = resolveWelcomePlanType(organizationInfo, variant);
  const planLabel = formatOrganizationPlanType(planType);
  const billingCycleLabel =
    organizationInfo?.billingCycle === 'monthly' ? 'monthly' : 'yearly';

  const welcomeVideoSrc = isTeamFlow
    ? teamStep === 1
      ? WELCOME_VIDEO_INVITE_TEAM_INTRO
      : WELCOME_VIDEO_CARTWHEEL_INTRO
    : WELCOME_VIDEO_CARTWHEEL_INTRO;

  const dismiss = () => {
    markWelcomeModalSeen(organizationInfo);
    getStartedCookie.remove();
    close();
  };

  return (
    <Modal
      title={
        <Text
          as="h2"
          token="display/20/medium"
          leading="title"
          className="text-primary text-balance"
        >
          {modalTitle}
        </Text>
      }
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          dismiss();
        }
      }}
      closeOnClickOutside={false}
      maxWidth={500}
    >
      <div className="flex flex-col gap-4">
        <Video
          key={isTeamFlow ? `team-${teamStep}` : 'individual'}
          src={welcomeVideoSrc}
          mode="autoplay"
          className="bg-secondary block aspect-452/254 w-full overflow-hidden rounded-lg"
        />

        <Text as="div" token="14" leading="body" className="text-primary">
          {showTrialIntro ? (
            <p>
              You're all set with your{' '}
              <span className="font-bold">
                7-day free trial of {planLabel} ({billingCycleLabel})!
              </span>{' '}
              Your trial is set to end on{' '}
              <span className="font-bold">
                {organizationInfo?.nextBillingDate ? (
                  <FormattedDate date={organizationInfo.nextBillingDate} />
                ) : (
                  'your trial end date'
                )}
              </span>
              . Here are your next steps:
            </p>
          ) : null}

          {showTeamChecklist ? (
            <ol className="mt-4 flex list-decimal flex-col gap-2 pl-7">
              <li>
                Go to your profile by selecting your avatar in the top right
              </li>
              <li>
                Head to the{' '}
                <a
                  href={getHomepageUrl('/profile/my-team')}
                  className="text-blue font-medium underline underline-offset-[2px]"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  My Team
                </a>{' '}
                tab
              </li>
              <li>
                Add seats to your team (when the trial ends you will be billed
                for each seat you have)
              </li>
              <li>Start inviting your team members</li>
            </ol>
          ) : null}

          {showIndividualTips ? (
            <>
              {isTeamFlow ? (
                <p>
                  After you invite some team members, here are some features to
                  check out:
                </p>
              ) : null}
              <p className="mt-4">
                To get started in Canvas, check out our key features{' '}
                <span className="font-bold">
                  Video-to-motion, text-to-motion
                </span>
                .
              </p>
              <p className="mt-4">
                We've also developed standalone apps, such as{' '}
                {/* <a
                   href={getHomepageUrl('/product/performance-editor')}
                  className="text-blue font-medium italic underline underline-offset-[2px]"
                >
                  Hermes
                </a>
                ,{' '} */}
                <a
                  href={getHomepageUrl('/product/cartographer')}
                  className="text-blue font-medium italic underline underline-offset-[2px]"
                >
                  Cartographer
                </a>
                ; accessible via selecting your account menu in the top right.
              </p>
              <p className="mt-4">We're excited for you to dig in.</p>
            </>
          ) : null}
        </Text>

        {isTeamFlow ? (
          <div className="mt-2 flex items-center justify-between gap-3">
            <Button variant="secondary" size="sm" onClick={dismiss}>
              Dismiss
            </Button>
            <div className="flex items-center gap-2">
              <WelcomePageDot
                slide={1}
                isActive={teamStep === 1}
                onClick={() => setTeamStep(1)}
              />
              <WelcomePageDot
                slide={2}
                isActive={teamStep === 2}
                onClick={() => setTeamStep(2)}
              />
            </div>
            {teamStep === 1 ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setTeamStep(2)}
              >
                Next
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={dismiss}>
                Done
              </Button>
            )}
          </div>
        ) : (
          <div className="flex justify-end">
            <Button variant="primary" size="sm" onClick={dismiss}>
              Done
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function WelcomePageDot({
  slide,
  onClick,
  isActive,
}: {
  slide: TeamWelcomeStep;
  onClick: () => void;
  isActive: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isActive ? `You are on step ${slide}` : `Go to step ${slide}`}
      className={`h-2 w-2 rounded-full border transition-colors ${
        isActive
          ? 'border-blue bg-blue'
          : 'border-gray-45 hover:bg-gray-45 bg-white'
      }`}
    />
  );
}

// Utils: --------------------------------------------------------------------

export function getWelcomeModalPreviewVariant(): WelcomeModalVariant | null {
  const value = new URLSearchParams(window.location.search).get(
    WELCOME_MODAL_PREVIEW_PARAM,
  );

  if (value === 'team' || value === 'individual') {
    return value;
  }

  return null;
}

function getWelcomeModalVariantFromPlan(
  organizationInfo?: OrganizationInfo | null,
): WelcomeModalVariant | null {
  if (
    !organizationInfo?.organizationID ||
    organizationInfo.inStripeFreeTrialPeriod !== true ||
    !organizationInfo.nextBillingDate
  ) {
    return null;
  }

  return welcomeModalVariantForWorkspaceTrialPlan(organizationInfo.planType);
}

export function getWelcomeModalVariant(
  organizationInfo?: OrganizationInfo | null,
): WelcomeModalVariant | null {
  return (
    getWelcomeModalPreviewVariant() ??
    getWelcomeModalVariantFromPlan(organizationInfo)
  );
}

function getWelcomeModalSeenKey(
  organizationInfo?: OrganizationInfo | null,
): string | null {
  const variant = getWelcomeModalVariantFromPlan(organizationInfo);

  if (!variant) {
    return null;
  }

  return `${TRIAL_WELCOME_STORAGE_KEY}:${organizationInfo?.organizationID ?? ''}:${organizationInfo?.planType ?? ''}:${organizationInfo?.nextBillingDate ?? ''}`;
}

export function hasSeenWelcomeModal(
  organizationInfo?: OrganizationInfo | null,
): boolean {
  if (getWelcomeModalPreviewVariant()) {
    return false;
  }

  const key = getWelcomeModalSeenKey(organizationInfo);

  if (!key) {
    return true;
  }

  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function markWelcomeModalSeen(
  organizationInfo?: OrganizationInfo | null,
) {
  if (getWelcomeModalPreviewVariant()) {
    return;
  }

  const key = getWelcomeModalSeenKey(organizationInfo);

  if (!key) {
    return;
  }

  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Ignore localStorage errors and continue with the dismiss flow.
  }
}

function resolveWelcomePlanType(
  organizationInfo: OrganizationInfo | null | undefined,
  variant: WelcomeModalVariant,
): OrganizationPlanType {
  const planType = organizationInfo?.planType;
  if (isTrialWelcomeWorkspacePlan(planType)) {
    return planType;
  }

  return WELCOME_PLAN_TYPES[variant];
}
